import { Children, type PropsWithChildren, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { type SharedValue } from 'react-native-reanimated';

import { useResponsive } from '@sg/garnish';

import { MenuHeader } from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useDeliveryDisclosure } from '@order/shared-hooks';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import { MenuContentMoreInfoModal, useMenuContentMoreInfoModal } from '../MenuContentMoreInfoModal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentHeader = (props: Props) => {
  const {
    restaurantData,
    locationName,
    orderChannel,
    deliveryDetailsData,
    children,
    menuGridScrollOffsetSV,
    compact,
    dataSet,
    changeLocation,
    onContactUs,
  } = props;

  const hasChildren = Children.count(children) > 0;

  const {
    flexMessage,
    showDeliveryFeeDisclosure,
    hours,
    utcOffset,
    deliveryFee: restaurantDeliveryFee,
  } = restaurantData;

  // ───────────────────────────────────────────────────────────────────────────

  const router = useRouter();
  const { minWidth } = useResponsive();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const moreInfoModal = useMenuContentMoreInfoModal();
  const { deliveryDisclosure } = useDeliveryDisclosure({ restaurantDeliveryFee }); // prettier-ignore

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const openContactUs = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  const handleContactUs = useCallback(() => {
    onContactUs?.();
    moreInfoModal.hideMoreInfoModal();

    // NOTE: To ensure modal is completely hidden before navigating.
    requestIdleCallback(() => {
      openContactUs();
    });
  }, [moreInfoModal, onContactUs, openContactUs]);

  const handleChangeLocation = useCallback(() => {
    if (changeLocation) {
      changeLocation();

      return;
    }

    router.navigate(isNavbarV2Enabled ? '/location-selection' : '/locations');
  }, [changeLocation, isNavbarV2Enabled, router]);

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const isDelivery = orderChannel === 'delivery';
  const isOutpost = orderChannel === 'outpost';

  const hasDeliveryDisclosure = Boolean(deliveryDisclosure);
  const hasFlexMessage = Boolean(flexMessage);
  const shouldRenderMoreInfoModal = !isDelivery;

  const shouldRenderFlexMessage = !isDelivery && minWidth.isSM && hasFlexMessage; // prettier-ignore
  const shouldRenderDeliveryFeeDisclosure = isDelivery && showDeliveryFeeDisclosure && hasDeliveryDisclosure; // prettier-ignore
  const shouldRenderHeaderNotices = shouldRenderDeliveryFeeDisclosure || shouldRenderFlexMessage; // prettier-ignore
  const shouldRenderWithoutTopSpacing = minWidth.isSM && isNavbarV2Enabled;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <MenuHeader.Container
        menuGridScrollOffsetSV={menuGridScrollOffsetSV}
        compact={compact}
        withoutTopSpacing={shouldRenderWithoutTopSpacing}
      >
        {minWidth.isSM ? (
          <MenuHeader.ContentContainer dataSet={dataSet}>
            <MenuHeader.DetailsText>
              <MenuHeader.DetailsTextItem>
                <MenuHeader.DetailsLocationText
                  locationName={locationName}
                  orderChannel={orderChannel}
                />
              </MenuHeader.DetailsTextItem>

              {isDelivery && deliveryDetailsData?.completeAddress ? (
                <MenuHeader.DetailsTextItem withSeparator>
                  {deliveryDetailsData.completeAddress}
                </MenuHeader.DetailsTextItem>
              ) : null}

              {isDelivery && deliveryDetailsData?.deliveryEstimate ? (
                <MenuHeader.DetailsTextItem withSeparator>
                  <FormattedMessage
                    {...messages.deliveryEtaText}
                    values={{ eta: deliveryDetailsData.deliveryEstimate }}
                  />
                </MenuHeader.DetailsTextItem>
              ) : null}

              {isDelivery ? null : (
                <MenuHeader.DetailsStoreHoursText
                  storeHours={hours.store}
                  permanentHours={hours.permanent}
                  utcOffsetHours={utcOffset ?? undefined}
                  orderChannel={orderChannel}
                />
              )}
            </MenuHeader.DetailsText>

            <MenuHeader.ChangeLocationButton onPress={handleChangeLocation} />

            {shouldRenderMoreInfoModal ? (
              <MenuHeader.MoreInfoButton onPress={moreInfoModal.showMoreInfoModal} />
            ) : null}
          </MenuHeader.ContentContainer>
        ) : null}

        {shouldRenderHeaderNotices ? (
          <MenuHeader.AlertsContainer>
            {shouldRenderFlexMessage ? <MenuHeader.Alert>{flexMessage}</MenuHeader.Alert> : null}

            {shouldRenderDeliveryFeeDisclosure ? (
              <MenuHeader.Alert sizeMatch={['14']}>{deliveryDisclosure}</MenuHeader.Alert>
            ) : null}
          </MenuHeader.AlertsContainer>
        ) : null}

        {hasChildren ? <View style={styles.contentContainer}>{children}</View> : null}
      </MenuHeader.Container>

      {shouldRenderMoreInfoModal ? (
        <MenuContentMoreInfoModal
          isVisible={moreInfoModal.isMoreInfoModalVisible}
          onClose={moreInfoModal.hideMoreInfoModal}
          locationName={locationName}
          restaurant={restaurantData}
          orderChannel={orderChannel}
          onContactUs={isOutpost ? handleContactUs : undefined} // NOTE: Outpost prefers to use the chatbot instead of the phone number.
        />
      ) : null}
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu Header | Delivery ETA text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {},
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  restaurantData: MenuContentRestaurantData;
  locationName: string;
  orderChannel: MenuContentOrderChannel;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  menuGridScrollOffsetSV?: SharedValue<number>;
  compact?: boolean;
  dataSet?: Record<string, string>;
  changeLocation?: () => void;
  onContactUs?: () => void;
}>;
